import { useRef, useState } from "react";
import { api, apiError, fmt } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Upload, X, Check, Clock, AlertTriangle } from "lucide-react";

const ACCEPT = {
  image: "image/*",
  video: "video/mp4,video/webm,video/quicktime",
  any: "image/*,video/mp4,video/webm,video/quicktime",
};

const isVideo = (f) => (f.content_type || "").startsWith("video/");

function StepUploader({ step, files, onChange, testid }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (picked.length === 0) return;
    if (files.length + picked.length > step.max_files) {
      toast.error(`Up to ${step.max_files} file${step.max_files > 1 ? "s" : ""} for this step`);
      return;
    }
    setBusy(true);
    try {
      const out = [];
      for (const file of picked) {
        const cap = (file.type || "").startsWith("video/") ? 64 : 8;
        if (file.size > cap * 1024 * 1024) {
          toast.error(`${file.name} is ${(file.size / 1048576).toFixed(1)}MB — keep it under ${cap}MB`);
          continue;
        }
        const body = new FormData();
        body.append("file", file);
        const { data } = await api.post("/uploads/proof", body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        out.push({ url: data.url, content_type: data.content_type });
      }
      if (out.length) {
        onChange([...files, ...out]);
        toast.success(`${out.length} file${out.length > 1 ? "s" : ""} attached`);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (step.proof_media === "none") return null;

  return (
    <div data-testid={testid}>
      <input ref={fileRef} type="file" multiple={step.max_files > 1}
        accept={ACCEPT[step.proof_media] || ACCEPT.any} className="hidden"
        onChange={pick} data-testid={`${testid}-input`} />
      <Button type="button" variant="outline" className="w-full rounded-none" disabled={busy}
        onClick={() => fileRef.current?.click()} data-testid={`${testid}-btn`}>
        <Upload className="mr-2 h-4 w-4" />
        {busy ? "Uploading…" : `Attach proof (${files.length}/${step.max_files})`}
      </Button>
      {files.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {files.map((f, i) => (
            <div key={f.url} className="relative h-24 overflow-hidden border border-border bg-secondary/40">
              {isVideo(f) ? (
                <video src={f.url} className="h-full w-full object-cover">
                  <track kind="captions" />
                </video>
              ) : (
                <img src={f.url} alt="proof" className="h-full w-full object-cover" />
              )}
              <button type="button"
                className="absolute right-1 top-1 bg-background/80 p-1 text-red-400"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
                data-testid={`${testid}-remove-${i}`}>
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const StepState = ({ status }) => {
  if (status === "approved")
    return <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-emerald-400">
      <Check className="h-3 w-3" /> Approved</span>;
  if (status === "pending_review")
    return <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-amber-400">
      <Clock className="h-3 w-3" /> In review</span>;
  if (status === "rejected")
    return <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-red-400">
      <AlertTriangle className="h-3 w-3" /> Try again</span>;
  return null;
};

export const QuestStepsDialog = ({ quest, onClose, onDone }) => {
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const single = quest?.step_mode === "single";
  const submitted = quest?.attempt?.steps || {};
  const steps = quest?.steps || [];

  const stepDraft = (key) => draft[key] || { files: [], fields: {}, note: "" };
  const patch = (key, p) => setDraft((d) => ({ ...d, [key]: { ...stepDraft(key), ...p } }));

  const sendOne = async (step) => {
    setBusy(true);
    try {
      const d = stepDraft(step.key);
      await api.post(`/quests/${quest.id}/submit-step`, {
        step_key: step.key, files: d.files, fields: d.fields, note: d.note,
      });
      toast.success("Sent for review — points drop as soon as staff approve it.");
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const ready = steps.filter((s) => {
    const state = submitted[s.key]?.status || "todo";
    if (state === "pending_review" || state === "approved") return false;
    const d = draft[s.key];
    return !!d && (d.files.length > 0 || d.note || Object.values(d.fields).some((v) => v !== ""));
  });

  const sendReady = async () => {
    setBusy(true);
    let sent = 0;
    try {
      for (const step of ready) {
        const d = stepDraft(step.key);
        await api.post(`/quests/${quest.id}/submit-step`, {
          step_key: step.key, files: d.files, fields: d.fields, note: d.note,
        });
        sent += 1;
      }
      toast.success(`${sent} step${sent > 1 ? "s" : ""} sent for review.`);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
      if (sent) onDone();
    } finally {
      setBusy(false);
    }
  };

  const sendAll = async () => {
    setBusy(true);
    try {
      await api.post(`/quests/${quest.id}/submit-all`, {
        steps: steps.map((s) => ({
          step_key: s.key, files: stepDraft(s.key).files,
          fields: stepDraft(s.key).fields, note: stepDraft(s.key).note,
        })),
      });
      toast.success("Submission received — staff will review all of it together.");
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!quest} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-none border-border bg-card sm:max-w-xl"
        data-testid="quest-steps-dialog">
        <DialogHeader>
          <DialogTitle>{quest?.title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {single
            ? "Upload everything below, then send it in one go."
            : "Do the steps in any order and upload proof for each one you've finished — every step pays out as soon as staff approve it, no waiting in between."}
        </p>

        <div className="space-y-4">
          {steps.map((s, i) => {
            const sub = submitted[s.key];
            const state = sub?.status || "todo";
            const editable = state === "todo" || state === "rejected";
            const d = stepDraft(s.key);
            return (
              <div key={s.key} className="border border-border p-4"
                data-testid={`quest-step-${i}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-primary">
                      Step {i + 1} of {steps.length}
                    </div>
                    <h4 className="mt-1 text-sm font-semibold">{s.label}</h4>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="mono text-sm text-primary">+{fmt(s.points)}</span>
                    <StepState status={state} />
                  </div>
                </div>
                {s.instruction && <p className="mt-2 text-xs text-muted-foreground">{s.instruction}</p>}
                {sub?.reject_reason && (
                  <p className="mt-2 text-xs text-red-400">Staff note: {sub.reject_reason}</p>
                )}

                {editable && (
                  <div className="mt-3 space-y-3">
                    {(s.fields || []).map((f) => (
                      <Input key={f.key} className="rounded-none bg-secondary"
                        type={f.type === "number" ? "number" : "text"}
                        placeholder={f.label + (f.required === false ? " (optional)" : "")}
                        value={d.fields[f.key] ?? ""}
                        onChange={(e) => patch(s.key, { fields: { ...d.fields, [f.key]: e.target.value } })}
                        data-testid={`quest-step-field-${i}-${f.key}`} />
                    ))}
                    <StepUploader step={s} files={d.files}
                      onChange={(files) => patch(s.key, { files })}
                      testid={`quest-step-upload-${i}`} />
                    <Textarea className="rounded-none bg-secondary" placeholder="Note for staff (optional)"
                      value={d.note} onChange={(e) => patch(s.key, { note: e.target.value })}
                      data-testid={`quest-step-note-${i}`} />
                    {!single && (
                      <Button className="w-full rounded-none" disabled={busy}
                        onClick={() => sendOne(s)} data-testid={`quest-step-submit-${i}`}>
                        {busy ? "Sending…" : `Submit step ${i + 1}`}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {single && (
          <Button className="rounded-none" disabled={busy} onClick={sendAll} data-testid="quest-steps-submit-all">
            {busy ? "Sending…" : "Send everything for review"}
          </Button>
        )}

        {!single && ready.length > 1 && (
          <Button className="rounded-none" disabled={busy} onClick={sendReady}
            data-testid="quest-steps-submit-ready">
            {busy ? "Sending…" : `Submit ${ready.length} filled steps`}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};
